/**
 * Admin Ads Service - Campaign list, detail, actions, ledger
 * Base: /api/v1/admin/ads
 * Maps backend snake_case to frontend camelCase Campaign model.
 */
import api from './api';

const ADS_BASE = '/admin/ads';

// ========== Models (camelCase, frontend convention) ==========

export interface Campaign {
  id: string;
  title: string;
  type: 'SHOP' | 'ITEM';
  status: 'DRAFT' | 'ACTIVE' | 'PAUSED' | 'ENDED';
  merchantId: number;
  merchantName: string;
  totalBudgetPoints: number;
  spentPoints: number;
  remainingPoints: number;
  dailyBudgetPoints?: number | null;
  costPerSuccessOrderPoints: number;
  radiusKm?: number;
  itemCount?: number;
  startAt?: string | null;
  endAt?: string | null;
  createdAt: string;
  updatedAt: string;
  autoPauseReason?: string | null;
  adminNote?: string | null;
  attributedOrdersToday?: number;
  attributedOrdersTotal?: number;
  clicksTotal?: number;
}

export interface MerchantLite {
  id: number;
  name: string;
  phone?: string;
}

export interface StatsTotals {
  impressions: number;
  clicks: number;
  attributedOrders: number;
  spendPoints: number;
  conversionRate: number;
}

export interface LedgerRow {
  id: string;
  orderId: number;
  points: number;
  status: 'SUCCESS' | 'FAILED';
  reason?: string | null;
  createdAt: string;
}

export interface CampaignDetail {
  campaign: Campaign;
  merchant: MerchantLite | null;
  stats: { series?: unknown[]; totals: StatsTotals };
  ledger: LedgerRow[];
}

export interface AdsSummary {
  activeCampaigns: number;
  totalSpendToday: number;
  totalSpend7d: number;
  attributedOrdersToday: number;
  merchantsRunningAds: number;
}

// ========== List API params ==========

export interface AdsListParams {
  search?: string;
  type?: 'SHOP' | 'ITEM';
  status?: 'DRAFT' | 'ACTIVE' | 'PAUSED' | 'ENDED';
  budgetState?: 'near_limit' | 'out_of_budget' | 'healthy';
  dateFrom?: string;
  dateTo?: string;
  sortBy?: 'newest' | 'highest_cpso' | 'highest_spend' | 'most_orders';
  page?: number;
  pageSize?: number;
}

export interface AdsListResponse {
  data: Campaign[];
  meta: { page: number; pageSize: number; total: number; pages?: number };
}

// ========== Backend response shapes (snake_case) ==========

interface BackendCampaignRaw {
  id: string;
  merchant_id?: number;
  type?: string;
  status?: string;
  title?: string;
  total_budget_points?: number;
  spent_points?: number;
  remaining_budget?: number;
  daily_budget_points?: number | null;
  cost_per_success_order_points?: number;
  targeting_radius_km?: number;
  start_at?: string | null;
  end_at?: string | null;
  created_at?: string;
  updated_at?: string;
  merchant_name?: string | null;
  attributed_orders_today?: number;
  attributed_orders_total?: number;
  clicks_total?: number;
  item_ids?: number[];
  admin_note?: string | null;
}

// ========== Normalizers ==========

function toCampaign(raw: BackendCampaignRaw): Campaign {
  const total = Number(raw.total_budget_points) || 0;
  const spent = Number(raw.spent_points) || 0;
  const rawRemaining = Number(raw.remaining_budget);
  const remaining = Number.isFinite(rawRemaining)
    ? rawRemaining
    : total - spent;
  return {
    id: raw.id,
    title: (raw.title ?? '') as string,
    type: (raw.type ?? 'SHOP') as 'SHOP' | 'ITEM',
    status: (raw.status ?? 'DRAFT') as 'DRAFT' | 'ACTIVE' | 'PAUSED' | 'ENDED',
    merchantId: Number(raw.merchant_id) || 0,
    merchantName: (raw.merchant_name ?? '') as string,
    totalBudgetPoints: total,
    spentPoints: spent,
    remainingPoints: remaining,
    dailyBudgetPoints: raw.daily_budget_points,
    costPerSuccessOrderPoints: Number(raw.cost_per_success_order_points) || 0,
    radiusKm: raw.targeting_radius_km,
    itemCount: Array.isArray(raw.item_ids) ? raw.item_ids.length : undefined,
    startAt: raw.start_at,
    endAt: raw.end_at,
    createdAt: (raw.created_at ?? new Date().toISOString()) as string,
    updatedAt: (raw.updated_at ?? new Date().toISOString()) as string,
    attributedOrdersToday: raw.attributed_orders_today,
    attributedOrdersTotal: raw.attributed_orders_total,
    clicksTotal: Number(raw.clicks_total) || 0,
    adminNote: raw.admin_note,
  };
}

function toLedgerRow(raw: Record<string, unknown>): LedgerRow {
  return {
    id: String(raw.id ?? ''),
    orderId: Number(raw.order_id) || 0,
    points: Number(raw.points) || 0,
    status: (raw.status as 'SUCCESS' | 'FAILED') ?? 'SUCCESS',
    reason: raw.reason as string | null | undefined,
    createdAt: (raw.created_at as string) ?? '',
  };
}

// ========== API methods ==========

/** GET /admin/ads/summary */
export async function fetchAdsSummary(): Promise<AdsSummary> {
  const res = await api.get<{
    success: boolean;
    data?: AdsSummary;
    message?: string;
  }>(`${ADS_BASE}/summary`);
  if (!res.data.success || !res.data.data) {
    throw new Error(
      (res.data as { message?: string })?.message ?? 'Failed to fetch summary'
    );
  }
  return res.data.data;
}

/** GET /admin/ads/campaigns */
export async function fetchAdsCampaigns(
  params: AdsListParams
): Promise<AdsListResponse> {
  const query: Record<string, string | number | undefined> = {};
  if (params.search?.trim()) query.search = params.search.trim();
  if (params.type) query.type = params.type;
  if (params.status) query.status = params.status;
  if (params.budgetState) query.budgetState = params.budgetState;
  if (params.dateFrom) query.dateFrom = params.dateFrom;
  if (params.dateTo) query.dateTo = params.dateTo;
  if (params.sortBy) query.sortBy = params.sortBy;
  if (params.page != null) query.page = params.page;
  if (params.pageSize != null) query.pageSize = params.pageSize;

  const res = await api.get<{
    success: boolean;
    data?: {
      campaigns: BackendCampaignRaw[];
      pagination: {
        page: number;
        pageSize: number;
        total: number;
        pages?: number;
      };
    };
  }>(`${ADS_BASE}/campaigns`, { params: query });

  if (!res.data.success) {
    throw new Error(
      (res.data as { message?: string }).message ?? 'Failed to fetch campaigns'
    );
  }

  const d = res.data.data;
  const campaigns = (d?.campaigns ?? []).map(toCampaign);
  const pagination = d?.pagination ?? { page: 1, pageSize: 20, total: 0 };

  return {
    data: campaigns,
    meta: {
      page: pagination.page,
      pageSize: pagination.pageSize,
      total: pagination.total,
      pages: pagination.pages,
    },
  };
}

/** GET /admin/ads/campaigns/:id */
export async function fetchAdsCampaignById(
  id: string
): Promise<CampaignDetail> {
  const res = await api.get<{
    success: boolean;
    data?: BackendCampaignRaw & {
      merchant?: { id: number; name: string; phone?: string } | null;
      item_ids?: number[];
      clicks?: number;
      attributed_orders?: number;
    };
  }>(`${ADS_BASE}/campaigns/${id}`);

  if (!res.data.success || !res.data.data) {
    throw new Error(
      (res.data as { message?: string }).message ?? 'Campaign not found'
    );
  }

  const raw = res.data.data;
  const campaign = toCampaign(raw);
  if (raw.item_ids) campaign.itemCount = raw.item_ids.length;

  const [ledgerRes] = await Promise.all([
    api.get<{
      success: boolean;
      data?: { ledger: Record<string, unknown>[] };
    }>(`${ADS_BASE}/campaigns/${id}/ledger`, {
      params: { page: 1, pageSize: 50 },
    }),
  ]);

  const ledger: LedgerRow[] = (ledgerRes.data?.data?.ledger ?? []).map(r => {
    const row = toLedgerRow(r);
    if (!row.status) row.status = 'SUCCESS';
    return row;
  });

  return {
    campaign,
    merchant: raw.merchant
      ? {
          id: raw.merchant.id,
          name: raw.merchant.name,
          phone: raw.merchant.phone,
        }
      : null,
    stats: {
      totals: {
        impressions: 0,
        clicks: Number(raw.clicks) || 0,
        attributedOrders: Number(raw.attributed_orders) || 0,
        spendPoints: campaign.spentPoints,
        conversionRate:
          (Number(raw.clicks) || 0) > 0
            ? ((Number(raw.attributed_orders) || 0) /
                (Number(raw.clicks) || 1)) *
              100
            : 0,
      },
    },
    ledger,
  };
}

/** GET /admin/ads/campaigns/:id/ledger (paginated) */
export async function fetchAdsCampaignLedger(
  id: string,
  page = 1,
  pageSize = 20
): Promise<{
  data: LedgerRow[];
  meta: { page: number; pageSize: number; total: number };
}> {
  const res = await api.get<{
    success: boolean;
    data?: {
      ledger: Record<string, unknown>[];
      pagination: { page: number; pageSize: number; total: number };
    };
  }>(`${ADS_BASE}/campaigns/${id}/ledger`, { params: { page, pageSize } });

  if (!res.data.success) {
    throw new Error(
      (res.data as { message?: string }).message ?? 'Failed to fetch ledger'
    );
  }

  const d = res.data.data;
  const ledger = (d?.ledger ?? []).map(r => toLedgerRow(r));
  const pagination = d?.pagination ?? { page, pageSize, total: 0 };

  return {
    data: ledger,
    meta: {
      page: pagination.page,
      pageSize: pagination.pageSize,
      total: pagination.total,
    },
  };
}

/** POST /admin/ads/campaigns/:id/pause */
export async function pauseAdsCampaign(id: string): Promise<void> {
  const res = await api.post<{ success: boolean }>(
    `${ADS_BASE}/campaigns/${id}/pause`
  );
  if (!res.data.success)
    throw new Error(
      (res.data as { message?: string }).message ?? 'Failed to pause'
    );
}

/** POST /admin/ads/campaigns/:id/resume */
export async function resumeAdsCampaign(id: string): Promise<void> {
  const res = await api.post<{ success: boolean }>(
    `${ADS_BASE}/campaigns/${id}/resume`
  );
  if (!res.data.success)
    throw new Error(
      (res.data as { message?: string }).message ?? 'Failed to resume'
    );
}

/** POST /admin/ads/campaigns/:id/end */
export async function endAdsCampaign(id: string): Promise<void> {
  const res = await api.post<{ success: boolean }>(
    `${ADS_BASE}/campaigns/${id}/end`
  );
  if (!res.data.success)
    throw new Error(
      (res.data as { message?: string }).message ?? 'Failed to end'
    );
}

/** POST /admin/ads/campaigns/:id/note */
export async function postCampaignNote(
  id: string,
  note: string
): Promise<void> {
  const res = await api.post<{ success: boolean }>(
    `${ADS_BASE}/campaigns/${id}/note`,
    { note }
  );
  if (!res.data.success)
    throw new Error(
      (res.data as { message?: string }).message ?? 'Failed to save note'
    );
}

// ========== Stats series ==========

export interface StatsPoint {
  period: string;
  spend: number;
  orders: number;
  clicks: number;
}

export interface StatsSeriesResponse {
  series: StatsPoint[];
  range: '24h' | '7d' | '30d';
}

/** GET /admin/ads/campaigns/:id/stats?range=7d */
export async function fetchCampaignStats(
  id: string,
  range: '24h' | '7d' | '30d'
): Promise<StatsSeriesResponse> {
  const res = await api.get<{ success: boolean; data?: StatsSeriesResponse }>(
    `${ADS_BASE}/campaigns/${id}/stats`,
    { params: { range } }
  );
  if (!res.data.success || !res.data.data) {
    return { series: [], range };
  }
  return res.data.data;
}

// ========== Create / Update campaign ==========

export interface CreateCampaignPayload {
  merchant_id: number;
  type: 'SHOP' | 'ITEM';
  title: string;
  description?: string;
  total_budget_points: number;
  cost_per_success_order_points: number;
  daily_budget_points?: number | null;
  targeting_radius_km?: number;
  start_at?: string | null;
  end_at?: string | null;
  item_ids?: number[];
  status?: 'DRAFT' | 'ACTIVE';
}

export interface UpdateCampaignPayload {
  title?: string;
  description?: string;
  total_budget_points?: number;
  cost_per_success_order_points?: number;
  daily_budget_points?: number | null;
  targeting_radius_km?: number;
  start_at?: string | null;
  end_at?: string | null;
}

/** POST /admin/ads/campaigns */
export async function createAdsCampaign(
  payload: CreateCampaignPayload
): Promise<Campaign> {
  const res = await api.post<{ success: boolean; data?: BackendCampaignRaw }>(
    `${ADS_BASE}/campaigns`,
    payload
  );
  if (!res.data.success || !res.data.data) {
    throw new Error(
      (res.data as { message?: string }).message ?? 'Failed to create campaign'
    );
  }
  return toCampaign(res.data.data);
}

/** PUT /admin/ads/campaigns/:id */
export async function updateAdsCampaign(
  id: string,
  payload: UpdateCampaignPayload
): Promise<Campaign> {
  const res = await api.put<{ success: boolean; data?: BackendCampaignRaw }>(
    `${ADS_BASE}/campaigns/${id}`,
    payload
  );
  if (!res.data.success || !res.data.data) {
    throw new Error(
      (res.data as { message?: string }).message ?? 'Failed to update campaign'
    );
  }
  return toCampaign(res.data.data);
}

// ========== Merchant search ==========

export interface MerchantSearchResult {
  id: number;
  name: string;
  phone?: string;
  address?: string;
}

/** GET /admin/ads/merchants/search?q=... */
export async function fetchMerchantsSearch(
  q: string,
  limit = 20
): Promise<MerchantSearchResult[]> {
  const res = await api.get<{
    success: boolean;
    data?: MerchantSearchResult[];
  }>(`${ADS_BASE}/merchants/search`, { params: { q, limit } });
  if (!res.data.success) return [];
  return res.data.data ?? [];
}

// ========== Ads Ops & Fraud ==========

export interface AdsOpsSummary {
  ranking: {
    weights: { w1: number; w2: number; w3: number; w4: number };
    maxRadiusM: number;
    nearbyLimit: number;
    epsilon: number;
    precomputeCron: string;
  };
  fraud: {
    shadowbannedUsers: number;
    adCancelThreshold: number;
    adOrderCancellations24h: number;
  };
  wallet: {
    totalHeldPoints: number;
    transactions24h: { hold: number; settle: number; refund: number };
  };
  redis: {
    redisAvailable: boolean;
    activeAdsCount: number;
    geoIndexCount: number;
  };
}

export interface ShadowbannedUser {
  id: number;
  name: string;
  email: string | null;
  phone: string | null;
  is_active: boolean;
  is_shadowbanned: boolean;
  created_at: string;
}

export interface AdWalletTransaction {
  id: number;
  type: 'ADS_HOLD' | 'ADS_SETTLE' | 'ADS_REFUND';
  amount_gcoin: number;
  balance_after: number;
  reference_id: string | null;
  reference_type: string | null;
  description: string | null;
  merchant_id: number;
  merchant_name: string | null;
  created_at: string;
}

export async function fetchAdsOpsSummary(): Promise<AdsOpsSummary | null> {
  const res = await api.get<{ success: boolean; data?: AdsOpsSummary }>(
    `${ADS_BASE}/ops/summary`
  );
  return res.data.success ? res.data.data ?? null : null;
}

export async function runAdsRankingPrecompute(): Promise<{
  success: boolean;
  message?: string;
  data?: { count: number; skipped?: boolean };
}> {
  const res = await api.post<{
    success: boolean;
    message?: string;
    data?: { count: number; skipped?: boolean };
  }>(`${ADS_BASE}/ranking/precompute`);
  return res.data;
}

export async function runAdsCloseExpired(): Promise<{
  success: boolean;
  message?: string;
  data?: { closedCount: number };
}> {
  const res = await api.post<{
    success: boolean;
    message?: string;
    data?: { closedCount: number };
  }>(`${ADS_BASE}/close-expired`);
  return res.data;
}

export async function fetchShadowbannedUsers(params: {
  page?: number;
  pageSize?: number;
  search?: string;
}): Promise<{ users: ShadowbannedUser[]; total: number }> {
  const res = await api.get<{
    success: boolean;
    data?: ShadowbannedUser[];
    pagination?: { total: number };
  }>(`${ADS_BASE}/ops/shadowbanned-users`, { params });
  return {
    users: res.data.data ?? [],
    total: res.data.pagination?.total ?? 0,
  };
}

export async function fetchAdWalletTransactions(params: {
  page?: number;
  pageSize?: number;
  type?: string;
  merchantId?: number;
}): Promise<{ transactions: AdWalletTransaction[]; total: number }> {
  const res = await api.get<{
    success: boolean;
    data?: AdWalletTransaction[];
    pagination?: { total: number };
  }>(`${ADS_BASE}/ops/wallet-transactions`, { params });
  return {
    transactions: res.data.data ?? [],
    total: res.data.pagination?.total ?? 0,
  };
}

export async function patchUserShadowban(
  userId: number,
  isShadowbanned: boolean
): Promise<{ success: boolean; message?: string }> {
  const res = await api.patch<{ success: boolean; message?: string }>(
    `/admin/users/${userId}/shadowban`,
    { isShadowbanned }
  );
  return res.data;
}
